<template>
    <div class="discover-panel">
        <div class="discover-head">
            <div>
                <div class="discover-kicker">HISTORY</div>
                <div class="discover-title">点歌历史</div>
                <div class="discover-sub">查看自己的点歌记录或整个房间的历史。</div>
            </div>
            <button type="button" class="discover-close" title="关闭" @click="$emit('close')">
                <mu-icon value="close" :size="20"></mu-icon>
            </button>
        </div>

        <div class="discover-filter-row">
            <button
                type="button"
                class="discover-chip"
                :class="{ 'is-solid': viewMode === 'mine' }"
                @click="viewMode = 'mine'">
                只看自己
            </button>
            <button
                type="button"
                class="discover-chip"
                :class="{ 'is-solid': viewMode === 'all' }"
                @click="viewMode = 'all'">
                查看全部
            </button>
            <button
                v-if="isRoot || isAdmin"
                type="button"
                class="discover-chip"
                @click="clearHistory">
                清空
            </button>
        </div>

        <div class="discover-search-row">
            <input
                class="discover-input"
                v-model.trim="keyword"
                placeholder="搜索点歌人、歌手或歌名"
            >
        </div>

        <div class="discover-stats">
            <span>已显示 <b>{{ displayedCount }}</b> 条历史</span>
            <span>共 {{ totalCount }} 条</span>
        </div>

        <div class="discover-list" v-if="filteredHistory.length">
            <div
                class="discover-row"
                v-for="item in filteredHistory"
                :key="item.id || (item.musicId + '-' + item.pickTime)"
                :class="{ 'is-played': item.played }"
                role="button"
                tabindex="0"
                @click="rePick(item)"
                @keydown.enter.prevent="rePick(item)">
                <div class="discover-cover is-fallback" :style="coverStyle(item)">
                    <img
                        v-if="historyCover(item)"
                        :src="historyCover(item)"
                        alt=""
                        @error="onCoverError">
                </div>
                <div class="discover-info">
                    <div class="discover-name">
                        {{ item.name || '未知歌曲' }}
                        <span v-if="item.played" class="discover-badge">已播</span>
                    </div>
                    <div class="discover-meta">
                        {{ item.artist || '未知歌手' }}
                        <span class="discover-sep">·</span>
                        {{ formatPickerName(item) }}
                    </div>
                </div>
                <div class="discover-actions">
                    <button type="button" class="discover-pick-btn" :disabled="requestBusy(item)" @click.stop="rePick(item)">{{ requestLabel(item) }}</button>
                </div>
            </div>
        </div>
        <div class="discover-empty" v-else>
            <div class="discover-empty-title">{{ emptyTitle }}</div>
            <div class="discover-empty-desc">{{ emptyDesc }}</div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex'
    import { formatDisplayName } from '../utils'
    import {latestPickRequest} from '../pickRequests'

    export default {
        name: 'HistoryPanel',
        data() {
            return {
                viewMode: 'all',
                keyword: ''
            }
        },
        computed: {
            ...mapGetters({
                historyList: 'getHistoryList',
                isRoot: 'isSocketRoot',
                isAdmin: 'isSocketAdmin',
                userName: 'getSocketUserName',
                identityId: 'getIdentityId',
                pickRequests: 'getPickRequests',
                meSessionIds: 'getMeSessionIds',
                meNickNames: 'getMeNickNames',
                mePickedIds: 'getMePickedIds'
            }),
            totalCount() {
                return (this.historyList && this.historyList.length) ? this.historyList.length : 0
            },
            filteredHistory() {
                const list = Array.isArray(this.historyList) ? this.historyList : []
                const kw = (this.keyword || '').toLowerCase()
                return list.filter((item) => {
                    if (this.viewMode === 'mine' && !this.isMine(item)) {
                        return false
                    }
                    if (!kw) {
                        return true
                    }
                    const name = String((item && item.name) || '').toLowerCase()
                    const artist = String((item && item.artist) || '').toLowerCase()
                    const picker = String((item && (item.pickUserName || item.nickName)) || '').toLowerCase()
                    return name.indexOf(kw) !== -1 || artist.indexOf(kw) !== -1 || picker.indexOf(kw) !== -1
                })
            },
            displayedCount() {
                return this.filteredHistory.length
            },
            emptyTitle() {
                if (this.viewMode === 'mine') {
                    return '你还没有点过歌'
                }
                if (this.keyword) {
                    return '没有匹配的历史'
                }
                return '还没有点歌记录'
            },
            emptyDesc() {
                if (this.viewMode === 'mine') {
                    return '点一首歌后，会出现在「只看自己」里'
                }
                if (this.keyword) {
                    return '换个点歌人、歌手或歌名再试试'
                }
                return '点歌成功后会出现在这里，方便大家知道点过哪些歌'
            }
        },
        methods: {
            normalizeName(name) {
                if (name == null || name === 'null' || name === 'undefined') {
                    return ''
                }
                return String(name).trim()
            },
            formatPickerName(item) {
                return formatDisplayName(item && (item.pickUserName || item.nickName))
            },
            isMine(item) {
                if (!item) return false
                const owner = this.normalizeName(item.pickUserId || item.sessionId)
                if (this.identityId) return owner === this.identityId
                return !!owner && (this.meSessionIds || []).indexOf(owner) !== -1
            },
            requestFor(item) { return latestPickRequest(this.pickRequests, {...item, id: item.musicId || item.id}) },
            requestBusy(item) {
                const request = this.requestFor(item)
                return !this.$store.getters.getIdentityReady || !!(request && request.status === 'processing')
            },
            requestLabel(item) {
                const request = this.requestFor(item)
                if (request && request.status === 'processing') return '处理中'
                if (request && request.status === 'queued') return '已入队'
                return request && request.status === 'failed' ? '重试' : '点歌'
            },

            historyCover(item) {
                if (item && item.pictureUrl) {
                    return item.pictureUrl
                }
                if (item && item.album && item.album.pictureUrl) {
                    return item.album.pictureUrl
                }
                return ''
            },
            coverStyle(item) {
                const seed = String((item && (item.name || item.musicId)) || 'x')
                    .split('')
                    .reduce((sum, ch) => sum + ch.charCodeAt(0), 0)
                const h1 = seed % 360
                const h2 = (seed * 7) % 360
                return {
                    background: `linear-gradient(135deg, hsl(${h1}, 52%, 64%), hsl(${h2}, 38%, 40%))`
                }
            },
            onCoverError(e) {
                if (e && e.target) {
                    e.target.style.display = 'none'
                }
            },
            clearHistory() {
                if (!(this.isRoot || this.isAdmin)) {
                    this.$toast && this.$toast.message('仅管理员可清空历史')
                    return
                }
                const stompClient = this.$store.getters.getStompClient
                if (!stompClient || typeof stompClient.send !== 'function') {
                    this.$toast && this.$toast.error('尚未连接服务器')
                    return
                }
                stompClient.send('/music/history/clear', {}, '')
            },
            rePick(item) {
                if (!item) return
                const stompClient = this.$store.getters.getStompClient
                if (!this.$store.getters.getIsConnected || !stompClient || typeof stompClient.send !== 'function') {
                    this.$toast && this.$toast.error('尚未连接服务器')
                    return
                }
                const musicId = (item.musicId || item.music_id || '').toString().trim()
                const name = (item.name || '').toString().trim()
                if (!musicId && !name) {
                    this.$toast && this.$toast.error('历史记录缺少歌曲信息，无法点歌')
                    return
                }
                let source = (item.source || 'qq').toString().trim().toLowerCase()
                if (!source || source === 'undefined' || source === 'null') {
                    source = 'qq'
                }
                if (musicId && /^[a-zA-Z0-9]{14}$/.test(musicId)) {
                    source = 'qq'
                }
                const payload = {
                    name: name || musicId,
                    source: source,
                    sendTime: Date.now()
                }
                if (musicId) {
                    payload.id = musicId
                }
                this.$emit('pick', payload)
            }
        }
    }
</script>
